import arriereBackoffice from '@avantstay/arriere-clients/dist/arriereBackoffice'

import {
  GenerateGuestReviewResponseProps,
  RateAirbnbGuestProps,
  RateVrboGuestProps,
  ReplyToReviewProps,
  ReplyToReviewResult,
  RequestVrboGuestReviewProps,
} from '@arriere/types/BookingReview.types'

const actingHostFields = {
  userId: true,
  isAvantStayAccount: true,
  accountName: true,
  hostRole: true,
} as const

export const getBookingInfo = async (bookingId: string) => {
  return arriereBackoffice.queries.booking({
    __args: { bookingId },
    guestId: true,
    guestName: true,
    guestRate: {
      id: true,
      categories: {
        category: true,
        comment: true,
        rating: true,
        tags: true,
      },
      createdAt: true,
      createdBy: {
        name: true,
      },
      recommendedGuest: true,
      source: true,
      privateFeedback: true,
      publicReview: true,
      response: true,
    },
    guests: true,
    hash: true,
    home: {
      id: true,
      links: {
        listings: {
          kind: true,
          leadSource: true,
          url: true,
        },
        manual: true,
      },
      name: true,
      region: {
        id: true,
        name: true,
      },
      unitNumber: true,
    },
    homeId: true,
    hotel: { id: true, name: true },
    hotelRoomType: {
      hotelId: true,
      id: true,
      roomKind: true,
      title: true,
    },
    id: true,
    isRoomAssigned: true,
    links: {
      manual: true,
      reservationHub: true,
      zendeskLink: true,
    },
    otaBookingInfo: {
      otaBookingId: true,
    },
    messagesRequestGuestReview: {
      message: true,
    },
    period: { end: true, start: true },
    reviewStatus: {
      status: true,
      submittedAt: true,
    },
    source: true,
    rateGuestEligibility: {
      isEligible: true,
      ineligibilityReason: true,
    },
  })
}

export const rateVrboGuest = async ({ bookingId, ratings, recommendGuest }: RateVrboGuestProps) => {
  return arriereBackoffice.mutations.vrboDistribution.raw({
    __alias: 'rateVrboGuest',
    reviews: {
      rateGuest: {
        __args: {
          bookingId,
          ratings,
          recommendGuest,
        },
      },
    },
  })
}

export const rateAirbnbGuest = async ({ ratings, ...otherProps }: RateAirbnbGuestProps) => {
  return arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'rateAirbnbGuest',
    reviews: { rateGuest: { __args: { categoryRatings: ratings, ...otherProps } } },
  })
}

export const requestVrboGuestReview = async ({
  bookingId,
  message,
}: RequestVrboGuestReviewProps) => {
  return arriereBackoffice.mutations.booking.raw({
    __alias: 'requestVrboGuestReview',
    requestGuestReview: {
      __args: {
        bookingId,
        message,
      },
    },
  })
}

export const replyToAirbnbReview = async ({
  bookingId,
  reviewId,
  message,
}: ReplyToReviewProps): Promise<ReplyToReviewResult> => {
  const { errors, warnings } = await arriereBackoffice.mutations.airbnbDistribution.raw({
    __alias: 'replyToAirbnbReview',
    reviews: {
      replyToReview: {
        __args: {
          bookingId,
          reviewId,
          message,
        },
      },
    },
  })

  return { errors, warnings }
}

export const replyToVrboReview = async ({
  bookingId,
  reviewId,
  message,
}: ReplyToReviewProps): Promise<ReplyToReviewResult> => {
  const { errors, warnings } = await arriereBackoffice.mutations.vrboDistribution.raw({
    __alias: 'replyToVrboReview',
    reviews: {
      replyToReview: {
        __args: {
          bookingId,
          reviewId,
          message,
        },
        status: true,
      },
    },
  })

  return { errors, warnings }
}

export const generateGuestReviewResponse = async ({
  bookingId,
  reviewId,
  userId,
}: GenerateGuestReviewResponseProps) => {
  return arriereBackoffice.mutations.generateGuestReviewResponse({
    __alias: 'generateGuestReviewResponse',
    __args: {
      bookingId,
      reviewId,
      userId,
    },
    response: true,
  })
}

export const getReservationActingHost = async (confirmationCode: string) => {
  return arriereBackoffice.queries.airbnbDistribution
    .raw({
      __alias: 'getReservationActingHost',
      reservations: {
        actingHost: {
          __args: { confirmationCode },
          ...actingHostFields,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data.reservations.actingHost
    })
}

export const getReviewActionLogs = async (confirmationCode: string) => {
  return arriereBackoffice.queries.airbnbDistribution
    .raw({
      __alias: 'getReviewActionLogs',
      reservations: {
        reviewActionLogs: {
          __args: { confirmationCode },
          reviewId: true,
          actionKind: true,
          actionAuthor: {
            name: true,
            kind: true,
          },
          actingHost: {
            ...actingHostFields,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data.reservations.reviewActionLogs
    })
}
